/**
 * Fetches a resident's configuration and hands it to the form.
 *
 * Keyed on `dataUpdatedAt`, so a save that comes back remounts the form rather than merging into
 * state it has already derived from — the corrections the form applies are computed from the
 * loaded configuration, and reconciling them against a fresh one in place is more fragile than
 * starting over.
 */

import * as React from 'react';
import { FormPanel, Panel, Spinner } from '@ixicare/ui';
import { Profile, ProfileConfiguration } from '@ixicare/resources';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { failureMessage, hasFieldErrors } from '~utils/error';
import { UserAction } from '~config/toastNotification';
import UserProfileEntry from '~store/Profile/UserProfileEntry';
import ResidentConfigForm from './ResidentConfigForm';

export function useProfileConfiguration(profileId: number) {
  const { notifications } = useStores();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['profileConfiguration', profileId],
    queryFn: () => Profile.getProfileConfiguration(profileId),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const patchConfigMutation = useMutation({
    mutationFn: (body: ProfileConfiguration) => Profile.patchProfileConfiguration(profileId, body),
    onSuccess: (data) => {
      queryClient.setQueryData(['profileConfiguration', profileId], data);
      notifications.addUserActionNotification({
        type: UserAction.ProfileConfigurationUpdate,
        notificationType: 'success',
        message: t('profile-configuration.update.success'),
      });
    },
    onError: (error: Error) => {
      // A refusal that names the fields it is about is shown on those fields. A toast as well
      // would say the same thing twice, in the one place the carer cannot act on it.
      if (hasFieldErrors(error)) return;
      notifications.addUserActionNotification({
        type: UserAction.ProfileConfigurationUpdate,
        notificationType: 'error',
        message: failureMessage(error, t('profile-configuration.update.error')),
      });
    },
  });

  return {
    mutate: patchConfigMutation.mutate,
    isMutating: patchConfigMutation.isPending,
    // What the last save was refused for, so the form can mark the fields it named.
    saveError: patchConfigMutation.error ?? undefined,
    isLoading: query.isLoading,
    data: query.data,
    dataUpdatedAt: query.dataUpdatedAt,
  };
}
export const ResidentConfigurationForm: React.FC<{ profile: UserProfileEntry }> = ({ profile }) => {
  const query = useProfileConfiguration(profile.id);

  return (
    <Panel.Section>
      <Panel.Title>{t('profile-configuration.title')}</Panel.Title>
      <FormPanel>
        {!query.isLoading ? (
          <ResidentConfigForm
            key={query.dataUpdatedAt}
            profile={profile}
            isSubmitting={query.isMutating}
            onSubmit={query.mutate}
            saveError={query.saveError}
            configuration={query.data}
            siteUuid={profile.site?.uuid}
          />
        ) : (
          <Spinner />
        )}
      </FormPanel>
    </Panel.Section>
  );
};
